import FolderId from 'owa-service/lib/contract/FolderId';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import FolderNodeViewState from '../store/schema/FolderNodeViewState';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { SortColumn } from 'mini-mail-list-store/lib/store/schema/SortBy';

export const enum SearchRefinerScopeType {
    SearchRefinerScopeAll,
    SearchRefinerScopePrimaryMailbox,
    SearchRefinerScopeCurrentFolder,
    SearchRefinerScopeCurrentFolderAndSubfolders,
}

export const enum FolderView {
    ConversationView = 1,
    DateView,
    DateViewOnly,
    ItemGroupView,
    BundledView,
}

export const enum SortDirection {
    Ascending,
    Descending,
}

export interface FolderViewState {
    FolderId: FolderId;
    View: FolderView;
    Width: number;
    SortOrder: SortDirection;
    SortColumn: SortColumn;
    ReadingPanePosition: ReadingPanePosition;
    IsExpanded: boolean;
    SearchScope: SearchRefinerScopeType;
}

/**
 * Function which returns modified viewState or new viewState if not present in user config.
 * @param folderId folderId
 * @param userConfigurationFolderViewState viewState from user config
 * @param folderViewStateFromStore viewState from store
 */
export default function getNewOrModifiedFolderViewState(
    folderId: string,
    userConfigurationFolderViewState: FolderViewState,
    folderViewStateFromStore: FolderNodeViewState
): FolderViewState {
    const { isExpanded } = folderViewStateFromStore;
    // If there is a userConfigurationFolderViewState, just get a version of the
    // FolderViewState based on that with the properties we want modified from the store
    if (userConfigurationFolderViewState) {
        return {
            ...userConfigurationFolderViewState,
            IsExpanded: isExpanded,
        } as FolderViewState;
    }

    // If not, just try to create a new FolderViewState with the right properties
    const folder = getStore().folders.folderTable.get(folderId);
    if (folder) {
        return {
            FolderId: folder.FolderId,
            View: getDefaultFolderView(folder.FolderId.Id),
            Width: 350,
            SortOrder: SortDirection.Descending,
            SortColumn: SortColumn.Date,
            IsExpanded: isExpanded,
            ReadingPanePosition: getUserConfiguration().UserOptions.GlobalReadingPanePositionReact,
            SearchScope: getDefaultSearchScopeForFolderView(folder.FolderId.Id),
        } as FolderViewState;
    }

    return null;
}

/**
 * Utility function which returns the default FolderView for folder.
 * @param folderId folderId
 */
export function getDefaultFolderView(folderId: string): FolderView {
    const distinguishedFolderIdName = folderIdToName(folderId);

    switch (distinguishedFolderIdName) {
        case 'drafts':
        case 'junkemail':
        case 'outbox':
        case 'notes':
            return FolderView.DateViewOnly;
        case 'deleteditems':
            return FolderView.DateView;
        default:
            return FolderView.ConversationView;
    }
}

/**
 * Utility function which returns default SearchRefinerScopeType for folder.
 * @param folderId folderId
 */
export function getDefaultSearchScopeForFolderView(folderId: string): SearchRefinerScopeType {
    const folderName = folderIdToName(folderId);

    switch (folderName) {
        case 'inbox':
            return SearchRefinerScopeType.SearchRefinerScopeAll;
        default:
            return SearchRefinerScopeType.SearchRefinerScopeCurrentFolder;
    }
}
